import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames/bind';
import styles from './styles.styl';
import { withTooltip } from '@consta/uikit/withTooltip';
import { Text } from '@consta/uikit/Text';
import { TextViewEnum } from 'src/utils/enum';

export type TextViewType =
  | 'link'
  | 'alert'
  | 'primary'
  | 'brand'
  | 'ghost'
  | 'linkMinor'
  | 'secondary'
  | 'success'
  | 'warning';

export type TextSizeType =
  | 's'
  | '2xs'
  | 'xs'
  | 'm'
  | 'l'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl';

export type TextLintHeightType = 's' | '2xs' | 'xs' | 'm' | 'l';

export type TextDisplayType = 'inline' | 'block' | 'inlineBlock';

export type WeightType =
  | 'black'
  | 'bold'
  | 'semibold'
  | 'regular'
  | 'light'
  | 'thin';

export interface CustomTextTooltipProps {
  text: string | number;
  tooltipText?: string;
  className?: any;
  textView?: TextViewType;
  size?: TextSizeType;
  lineHeight?: TextLintHeightType;
  offsetHeight?: number;
  display?: TextDisplayType;
  weight?: WeightType;
  lineClamp?: number;
}

const cx = cn.bind(styles);

const CustomTextTooltip: React.FC<CustomTextTooltipProps> = React.memo(
  ({
    text,
    tooltipText,
    className,
    textView,
    size = 'xs',
    lineHeight = 'm',
    offsetHeight = 68,
    weight = null,
    lineClamp = 3,
  }) => {
    const TextWithTooltip = withTooltip({
      direction: 'upCenter',
      possibleDirections: ['upRight'],
      style: { WebkitLineClamp: lineClamp },
    })(Text);
    const [isTruncate, setTruncate] = useState<boolean>(false);
    const [isChecked, setChecked] = useState<boolean>(false);
    const ref = useRef(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      setChecked(true);
      if (ref.current && ref.current.offsetHeight > offsetHeight)
        setTruncate(true);
    });

    if (isChecked && !isTruncate && !tooltipText)
      return (
        <Text
          size={size}
          lineHeight={lineHeight}
          weight={weight}
          ref={ref}
          view={textView || TextViewEnum.PRIMARY}
          className={cx('CustomTextTooltip__indent', className)}
        >
          {text}
        </Text>
      );
    return (
      <TextWithTooltip
        lineHeight={lineHeight}
        className={cx('CustomTextTooltip__indentTooltip', className)}
        size={size}
        view={textView || TextViewEnum.PRIMARY}
        tooltipProps={{
          content: (
            <div className={cx('CustomTextTooltip')}>
              <Text
                size="xs"
                lineHeight="xs"
                className={cx('CustomTextTooltip__text')}
              >
                {tooltipText || text}
              </Text>
            </div>
          ),
          size: 'l',
        }}
      >
        <Text
          size={size}
          lineHeight={lineHeight}
          className={cx('CustomTextTooltip__textTooltip')}
        >
          {text}
        </Text>
      </TextWithTooltip>
    );
  },
);

CustomTextTooltip.displayName = ' CustomTextTooltip';
export default CustomTextTooltip;
